<template>
  <div class="inline-quiz-card">
    <div class="inline-quiz-icon-row" style="margin-bottom: 16px;">
      <span class="inline-quiz-icon">?</span>
      <p class="inline-quiz-title">Autoevaluación</p>
    </div>

    <div class="inline-quiz-questions">
      <div
        v-for="(q, qi) in block.questions"
        :key="qi"
        class="inline-quiz-question-card"
      >
        <p class="inline-quiz-question-text">{{ qi + 1 }}. {{ q.question }}</p>

        <!-- Answering: opciones seleccionables mientras no se verificó -->
        <div v-if="!checked" class="inline-quiz-options">
          <button
            v-for="(opt, optIdx) in q.options"
            :key="optIdx"
            class="inline-quiz-option-btn"
            :class="{ 'option-selected': selected[qi] === optIdx }"
            @click="selectOption(qi, optIdx)"
          >
            <span
              class="option-letter"
              :class="{ 'letter-selected': selected[qi] === optIdx }"
            >
              {{ LETTERS[optIdx] }}
            </span>
            <span
              class="option-text"
              :class="{ 'text-selected': selected[qi] === optIdx }"
            >
              {{ opt }}
            </span>
          </button>
        </div>

        <!-- Checked: resultado corregido por el servidor -->
        <template v-else>
          <div class="inline-quiz-result-row">
            <span
              class="inline-quiz-result-badge"
              :class="results[qi]?.isCorrect ? 'result-correct' : 'result-incorrect'"
            >
              {{ results[qi]?.isCorrect ? '✓' : '✗' }}
            </span>
            <div class="inline-quiz-result-body">
              <div class="inline-quiz-result-options">
                <div
                  v-for="(opt, optIdx) in q.options"
                  :key="optIdx"
                  class="inline-quiz-result-option"
                  :class="{
                    'option-correct': results[qi]?.isCorrect && selected[qi] === optIdx,
                    'option-wrong': !results[qi]?.isCorrect && selected[qi] === optIdx,
                  }"
                >
                  <span
                    class="option-indicator"
                    :class="{
                      'indicator-correct': results[qi]?.isCorrect && selected[qi] === optIdx,
                      'indicator-wrong': !results[qi]?.isCorrect && selected[qi] === optIdx,
                    }"
                  >
                    {{ selected[qi] === optIdx ? (results[qi]?.isCorrect ? '✓' : '✗') : '' }}
                  </span>
                  <span>{{ opt }}</span>
                </div>
              </div>
              <p v-if="results[qi]?.explanation" class="inline-quiz-explanation">
                {{ results[qi]?.explanation }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Score summary tras verificar -->
    <div v-if="checked" class="inline-quiz-header" style="margin-top: 16px;">
      <div class="inline-quiz-icon-row">
        <span class="inline-quiz-icon">?</span>
        <p class="inline-quiz-title">Resultado</p>
      </div>
      <span class="inline-quiz-score-text">
        {{ correct }} de {{ total }} correctas
      </span>
    </div>

    <div v-else class="inline-quiz-footer">
      <q-btn
        no-caps
        dense
        unelevated
        label="Verificar respuestas"
        :disable="!allAnswered"
        :loading="submitting"
        :style="{ background: allAnswered ? 'var(--oa-gold-full)' : 'var(--oa-bg-3)', color: allAnswered ? 'var(--color-white-cap)' : 'var(--oa-text-muted)', fontWeight: '600', fontSize: '13px', borderRadius: '8px', padding: '6px 16px' }"
        @click="verify"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { api } from 'boot/axios';

// NOTA (P-M4 / T-08-11): el bloque quiz que llega al alumno viene STRIPPEADO por
// GET /courses/:slug (Plan 02): la clave de respuestas y la explicación NO están
// presentes en runtime — llegan `undefined` por diseño. Este componente NUNCA debe
// corregir localmente contra la clave. La corrección viene EXCLUSIVAMENTE de la
// respuesta de POST /learn/quiz/grade (results[i].isCorrect / results[i].explanation).
// No "optimizar" esto a una comparación local: rompería el modelo de seguridad.
interface QuizQuestion {
  question: string;
  options: string[];
}
interface QuizBlockData {
  type: 'quiz';
  questions: QuizQuestion[];
}
interface QuizResult {
  isCorrect: boolean;
  explanation: string | null;
}

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

const props = defineProps<{ block: QuizBlockData; lessonId: string; blockIndex: number }>();

const selected = ref<Record<number, number>>({});
const checked = ref(false);
const results = ref<QuizResult[]>([]);
const correct = ref(0);
const total = ref(0);
const submitting = ref(false);

const allAnswered = computed(
  () => Object.keys(selected.value).length === props.block.questions.length,
);

function selectOption(qIdx: number, optIdx: number) {
  if (checked.value) return;
  selected.value = { ...selected.value, [qIdx]: optIdx };
}

async function verify() {
  submitting.value = true;
  try {
    // -1 marca "sin responder"; la corrección real la computa el servidor.
    const answers = props.block.questions.map((_, i) => selected.value[i] ?? -1);
    const { data } = await api.post('/learn/quiz/grade', {
      lessonId: props.lessonId,
      blockIndex: props.blockIndex,
      answers,
    });
    results.value = data.results;
    correct.value = data.correct;
    total.value = data.total;
    checked.value = true;
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.inline-quiz-card {
  background: color-mix(in srgb, var(--oa-gold-full) 3%, var(--oa-bg-2));
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  border-radius: var(--oa-radius-xl);
  padding: 20px;
  margin: 16px 0;
}

.inline-quiz-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.inline-quiz-icon-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.inline-quiz-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--oa-text-sm);
  color: var(--oa-gold-full);
}

.inline-quiz-title {
  font-weight: 600;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0;
}

.inline-quiz-score-text {
  font-size: var(--oa-text-sm);
  font-weight: 500;
  color: var(--oa-text-primary);
}

.inline-quiz-result-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.inline-quiz-result-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}

.result-correct {
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.result-incorrect {
  background: color-mix(in srgb, var(--oa-danger) 10%, transparent);
  color: var(--oa-danger);
}

.inline-quiz-result-body {
  flex: 1;
  min-width: 0;
}

.inline-quiz-result-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inline-quiz-result-option {
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--oa-text-muted);
  border: 1px solid transparent;
}

.option-correct {
  background: color-mix(in srgb, var(--oa-success) 8%, transparent);
  color: var(--oa-success);
  border-color: color-mix(in srgb, var(--oa-success) 15%, transparent);
}

.option-wrong {
  background: color-mix(in srgb, var(--oa-danger) 8%, transparent);
  color: var(--oa-danger);
  border-color: color-mix(in srgb, var(--oa-danger) 15%, transparent);
}

.option-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.indicator-correct {
  border-color: color-mix(in srgb, var(--oa-success) 30%, transparent);
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.indicator-wrong {
  border-color: color-mix(in srgb, var(--oa-danger) 30%, transparent);
  background: color-mix(in srgb, var(--oa-danger) 10%, transparent);
  color: var(--oa-danger);
}

.inline-quiz-explanation {
  font-size: 12px;
  color: var(--oa-text-secondary);
  margin: 8px 0 0 0;
  font-style: italic;
  line-height: 1.5;
}

.inline-quiz-questions {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.inline-quiz-question-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-lg);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  padding: 16px;
}

.inline-quiz-question-text {
  font-weight: 500;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0 0 12px 0;
}

.inline-quiz-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.inline-quiz-option-btn {
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  background: transparent;
  cursor: pointer;
  transition: all var(--oa-duration-fast) ease;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
    background: color-mix(in srgb, var(--oa-gold-full) 3%, transparent);
  }
}

.option-selected {
  border-color: var(--oa-gold-full) !important;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent) !important;
}

.option-letter {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  color: var(--oa-text-muted);
  transition: all var(--oa-duration-fast) ease;
}

.letter-selected {
  background: var(--oa-gold-full) !important;
  color: var(--color-white-cap) !important;
}

.option-text {
  padding-top: 2px;
  color: var(--oa-text-secondary);
  transition: color var(--oa-duration-fast) ease;
}

.text-selected {
  color: var(--oa-text-primary) !important;
  font-weight: 500;
}

.inline-quiz-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
